import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from '../login/LoginForm';
import { 
  Cog6ToothIcon,
  Squares2X2Icon,
  TableCellsIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    defaultView: 'cards',
    itemsPerPage: 10,
    autoRefreshInterval: 15,
    notificationsEnabled: true,
  });

  useEffect(() => {
    // Load settings from localStorage
    const saved = localStorage.getItem('userPreferences');
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    }
  }, []);

  const updateSetting = (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    localStorage.setItem('userPreferences', JSON.stringify(updated));
  };

  if (!user) {
    return <LoginForm />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Header */}
          <div className="px-6 py-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Cog6ToothIcon className="h-6 w-6 text-primary-600" />
              <h1 className="text-2xl font-bold text-gray-900">
                Settings
              </h1>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Manage your application preferences and settings
            </p>
          </div>

          {/* Settings Content */}
          <div className="px-6 py-6 space-y-8">
            {/* Display Preferences */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Display Preferences
              </h2>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Default View
                  </label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => updateSetting('defaultView', 'cards')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        settings.defaultView === 'cards'
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Squares2X2Icon className="h-4 w-4" />
                      Cards
                    </button>
                    <button
                      onClick={() => updateSetting('defaultView', 'table')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        settings.defaultView === 'table'
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <TableCellsIcon className="h-4 w-4" />
                      Table
                    </button>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Items Per Page
                  </label>
                  <select
                    value={settings.itemsPerPage}
                    onChange={(e) => updateSetting('itemsPerPage', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Auto-Refresh */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Auto-Refresh
              </h2>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <ArrowPathIcon className="h-5 w-5 text-gray-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Auto-Refresh Interval
                    </p>
                    <p className="text-xs text-gray-500">
                      Automatically refresh data every N seconds
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="5"
                    max="60"
                    step="5"
                    value={settings.autoRefreshInterval}
                    onChange={(e) => updateSetting('autoRefreshInterval', parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium text-gray-900 w-12 text-right">
                    {settings.autoRefreshInterval}s
                  </span>
                </div>
              </div>
            </div>

            {/* Notifications */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Notifications
              </h2>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Enable Notifications
                    </p>
                    <p className="text-xs text-gray-500">
                      Show toast notifications for actions
                    </p>
                  </div>
                  <button
                    onClick={() => updateSetting('notificationsEnabled', !settings.notificationsEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.notificationsEnabled ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.notificationsEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

